/**
 * How many modal scrims are currently on screen.
 *
 * Its one consumer is the Windows caption-button dim (see
 * `../hooks/useCaptionButtonScrim.ts`): those buttons are composited above the
 * page, so a dialog's `bg-black/45` overlay cannot reach them and the main
 * process has to repaint them instead. The overlays only report that they are
 * up — nothing in `components/ui` should know about window chrome, and a
 * dialog rendered in a unit test must not reach for `electronAPI`.
 *
 * A count rather than a flag: dialogs stack (a confirm prompt opened from a
 * dialog), and each overlay leaves on its own exit animation, so the last one
 * out is what ends the scrim.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

interface ModalScrimState {
  openScrimCount: number;
  openScrim: () => void;
  closeScrim: () => void;
}

export const useModalScrimStore = create<ModalScrimState>((set) => ({
  openScrimCount: 0,
  openScrim: () => set((state) => ({ openScrimCount: state.openScrimCount + 1 })),
  closeScrim: () =>
    set((state) => ({ openScrimCount: Math.max(0, state.openScrimCount - 1) })),
}));

/** Call from a component that renders a full-window modal scrim. */
export function useModalScrimPresence(): void {
  useEffect(() => {
    const { openScrim, closeScrim } = useModalScrimStore.getState();
    openScrim();
    return closeScrim;
  }, []);
}
